import { Users } from "lucide-react-native";
import { FC } from "react";

import {
  Box,
  HStack,
  Pressable,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { StudyType } from "@/types/resources";
import { GroupSize } from "@/types/utils";

export const QUALI_PRESETS: GroupSize[] = [10, 20, 30, 40, 50];
export const QUANTI_PRESETS: GroupSize[] = [100, 200, 500, 1000, 1500];

interface GroupSizePickerProps {
  disabled?: boolean;
  onChange: (size: GroupSize) => void;
  size: number;
  studyType: StudyType | null;
}

/**
 * Sample-size control of the group editor's dark summary card: a slider
 * bounded by the study type plus quick preset pills.
 */
const GroupSizePicker: FC<GroupSizePickerProps> = ({
  disabled = false,
  onChange,
  size,
  studyType,
}) => {
  const { browserLocale, i18n } = useLocalization();
  const isQuantitative = studyType === "quantitative";
  const presets = isQuantitative ? QUANTI_PRESETS : QUALI_PRESETS;
  const sliderMin = isQuantitative ? 100 : 10;
  const sliderMax = isQuantitative ? 1500 : 50;
  const sliderStep = isQuantitative ? 100 : 5;

  return (
    <Box className="p-3 rounded-lg bg-white/[0.08] border border-white/10">
      <HStack className="items-center gap-2 mb-2.5">
        <Users color="rgb(var(--color-primary-400))" size={14} />
        <Text className="text-xs font-medium text-white/70">
          {i18n.t("audienceBuilder.sampleSize")}
        </Text>
      </HStack>
      <HStack className="items-center gap-3">
        <Box className="flex-1">
          <Slider
            isDisabled={disabled}
            maxValue={sliderMax}
            minValue={sliderMin}
            onChange={(value) => onChange(value as GroupSize)}
            size="sm"
            step={sliderStep}
            value={size}
          >
            <SliderTrack className="bg-white/20">
              <SliderFilledTrack className="bg-primary-400" />
            </SliderTrack>
            <SliderThumb className="bg-white border-2 border-primary-400 shadow-sm" />
          </Slider>
        </Box>
        <HStack className="items-center gap-1.5 w-16 justify-end">
          <Users color="rgb(var(--color-primary-400))" size={14} />
          <Text className="text-white font-semibold text-sm text-right">
            {i18n.numberToDelimited(size, {
              delimiter: browserLocale.digitGroupingSeparator!,
            })}
          </Text>
        </HStack>
      </HStack>
      <HStack className="justify-between mt-1.5">
        <Text className="text-xs text-white/40">
          {sliderMin.toLocaleString()}
        </Text>
        <Text className="text-xs text-white/40">
          {sliderMax.toLocaleString()}
        </Text>
      </HStack>
      <HStack className="gap-1.5 mt-2.5">
        {presets.map((n) => (
          <Pressable
            className={
              disabled
                ? "flex-1 cursor-default data-[disabled=true]:opacity-100 web:pointer-events-none"
                : "flex-1"
            }
            disabled={disabled}
            key={n}
            onPress={() => onChange(n)}
          >
            <Box
              className={`py-1 rounded-md items-center ${
                size === n ? "bg-primary-400" : "bg-white/10"
              }`}
            >
              <Text
                className={`text-xs font-medium ${
                  size === n ? "text-white" : "text-white/60"
                }`}
              >
                {n.toLocaleString()}
              </Text>
            </Box>
          </Pressable>
        ))}
      </HStack>
    </Box>
  );
};

export default GroupSizePicker;
